import { DocketDisplayItem, DocketDisplayItemType } from '../common/types';
import { LayoutStrategy } from '@oolio-group/domain';
import {
  DEFAULT_ROWS_PER_PAGE,
  DOCKET_HEIGHT,
  DOCKET_WIDTH,
} from '../common/constants';
import theme from '../common/theme';

export interface Column {
  column: number;
  rows: {
    row: number;
    items: DocketDisplayItem[];
  }[];
}

export interface PaginatedDocketItems {
  page: number;
  columns: Column[];
}
/**
 *
 * @returns if the current docket is overflowing the column or not
 */
export const isOverflowingDocket = (
  items: DocketDisplayItem[],
  currentIndex: number,
  colHeight: number,
  currentHeight: number,
  continueHeight: number,
  scale: (n: number) => number,
  isGridLayout?: boolean,
): boolean => {
  const currentItem = items[currentIndex];
  /**
   * For grid layout, we allow flexible placement for most headers instead of forcing
   * them to group with their first item. This prevents "lonely headers" (like tags or
   * title bars) from appearing alone in their own column when they could fit with other content.
   *
   * Only semantically important grouping headers (COURSE, SEAT_NUMBER, CATEGORY,
   * NOTIFIER_HEADER) should
   * use the strict "header + first item" grouping logic, as these headers logically
   * organize related content.
   *
   * Other headers (DOCKET_HEADER, TRAINING_DOCKET, DOCKET_TITLE_BAR, tags) are more
   * informational and can be placed flexibly to optimize space usage in grid layout.
   */
  if (
    currentItem.type !== DocketDisplayItemType.COURSE &&
    currentItem.type !== DocketDisplayItemType.SEAT_NUMBER &&
    currentItem.type !== DocketDisplayItemType.CATEGORY &&
    currentItem.type !== DocketDisplayItemType.NOTIFIER_HEADER &&
    isGridLayout
  ) {
    return false;
  }
  if (
    currentItem.type === DocketDisplayItemType.DOCKET_HEADER ||
    currentItem.type === DocketDisplayItemType.COURSE ||
    currentItem.type === DocketDisplayItemType.TRAINING_DOCKET ||
    currentItem.type === DocketDisplayItemType.DOCKET_TITLE_BAR ||
    currentItem.type === DocketDisplayItemType.CATEGORY ||
    currentItem.type === DocketDisplayItemType.SEAT_NUMBER ||
    currentItem.type === DocketDisplayItemType.NOTIFIER_HEADER
  ) {
    const totalEligibleDocketHeight = getDocketHeightWithFirstItem(
      items,
      currentIndex,
    );
    /** We are doing -2 here because we want to account for the current item
    is header we want to account for next two items
    */
    const hasRemainingItems =
      currentItem.type === DocketDisplayItemType.NOTIFIER_HEADER
        ? getNotifierLeadEndIndex(items, currentIndex) < items.length - 1
        : currentIndex < items.length - 2;
    const effectiveContinueHeight = hasRemainingItems ? continueHeight : 0;
    if (
      scale(totalEligibleDocketHeight) +
        effectiveContinueHeight +
        currentHeight >
      colHeight
    ) {
      return true;
    }
  }
  return false;
};

/**
 *
 * @returns the total height of the docket including the first item
 */
const NOTIFIER_ROW_TYPES = [
  DocketDisplayItemType.NOTIFIER_HEADER,
  DocketDisplayItemType.NOTIFIER_PROFILE,
  DocketDisplayItemType.NOTIFIER_ITEM,
  DocketDisplayItemType.NOTIFIER_SUMMARY,
];

const getNotifierLeadEndIndex = (
  items: DocketDisplayItem[],
  headerIndex: number,
): number => {
  let end = headerIndex;
  for (let i = headerIndex + 1; i < items.length; i++) {
    const type = items[i].type;
    if (!NOTIFIER_ROW_TYPES.includes(type)) break;
    end = i;
    if (
      type === DocketDisplayItemType.NOTIFIER_ITEM ||
      type === DocketDisplayItemType.NOTIFIER_SUMMARY
    ) {
      break;
    }
  }
  return end;
};

const getDocketHeightWithFirstItem = (
  items: DocketDisplayItem[],
  currentIndex: number,
): number => {
  let height = items[currentIndex].height;

  if (items[currentIndex].type === DocketDisplayItemType.NOTIFIER_HEADER) {
    for (let i = currentIndex + 1; i < items.length; i++) {
      const type = items[i].type;
      if (!NOTIFIER_ROW_TYPES.includes(type)) break;
      height += items[i].height;
      if (
        type === DocketDisplayItemType.NOTIFIER_ITEM ||
        type === DocketDisplayItemType.NOTIFIER_SUMMARY
      ) {
        break;
      }
    }
    return height;
  }

  for (let i = currentIndex + 1; i < items.length; i++) {
    height += items[i].height;

    if (items[i].type === DocketDisplayItemType.ITEM) {
      break;
    }
  }
  return height;
};

/**
 *
 * @param layoutStrategy - The layout strategy to determine the columns rows per page.
 * @returns number of columns per page based on the layout strategy.
 */
export const getDefaultRowsPerPage = (
  layoutStrategy = LayoutStrategy.SINGLE_ROW,
) => {
  return DEFAULT_ROWS_PER_PAGE[layoutStrategy];
};

/**
 * How many dockets of a given size the viewport fits along one axis.
 * `DocketsLayout` caps what it draws this way, so anything previewing the
 * layout has to apply the same ceiling to stay honest.
 */
const getViewportCapacity = (
  extent: number,
  docketSize: number,
  scale: (n: number) => number,
): number => Math.floor(extent / scale(docketSize));

/**
 * Clamps a configured value to a maximum, when one is supplied.
 *
 * A null check, not a truthiness check: a viewport too small to fit a single
 * docket yields a maximum of 0, and that has to clamp to 0 rather than fall
 * through to the unclamped value.
 */
const clampToMax = (value: number, max?: number): number =>
  max == null ? value : Math.min(value, max);

/** The most rows the viewport can fit at the current scale. */
export const getMaxSupportedRows = (scale: (n: number) => number): number =>
  getViewportCapacity(theme.window.vh, DOCKET_HEIGHT, scale);

/**
 * The number of rows the KDS will actually render. Only GRID honours a
 * configured `rowsOnScreen`; every other strategy renders its own fixed
 * default regardless of what happens to be persisted, so a `rowsOnScreen`
 * left over from a previous Grid layout must not be shown as if it applied.
 *
 * `maxSupportedRows` mirrors the clamp `DocketsLayout` applies: a persisted
 * `rowsOnScreen` larger than the viewport can fit is never drawn, so it must
 * not be displayed either.
 */
export const getEffectiveRowsPerPage = (
  layoutStrategy: LayoutStrategy,
  rowsOnScreen: number,
  maxSupportedRows?: number,
): number => {
  const rows =
    layoutStrategy === LayoutStrategy.GRID
      ? rowsOnScreen
      : getDefaultRowsPerPage(layoutStrategy);
  return clampToMax(rows, maxSupportedRows);
};

/** The most columns the viewport can fit at the current scale. */
export const getMaxSupportedColumns = (scale: (n: number) => number): number =>
  getViewportCapacity(theme.window.vw, DOCKET_WIDTH, scale);

/**
 * The number of columns the KDS will actually render. `maxSupportedColumns`
 * mirrors the clamp `DocketsLayout` applies: a persisted `columnsOnScreen`
 * wider than the viewport can fit is never drawn, so it must not be displayed
 * either.
 */
export const getEffectiveColumns = (
  columnsOnScreen: number,
  maxSupportedColumns?: number,
): number => clampToMax(columnsOnScreen, maxSupportedColumns);
